import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import {
  HiOutlineInboxArrowDown,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineArrowPath,
  HiOutlineCalendarDays,
  HiOutlineMagnifyingGlass,
  HiOutlineTrophy
} from "react-icons/hi2";

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#E2E8F0",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626"
};

function Inbox({ user }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (user?.id) {
      loadInbox();
    }
  }, [user]);

  const loadInbox = async () => {
    try {
      setLoading(true);
      setError("");

      const [reqRes, appRes] = await Promise.all([
        api.get(`/requests/user/${user.id}`),
        api.get(`/appreciations/user/${user.id}`)
      ]);

      const requestRows = Array.isArray(reqRes.data) ? reqRes.data : [];
      const appreciationRows = Array.isArray(appRes.data) ? appRes.data : [];

      const formattedRequests = requestRows.map((r) => ({
        ...r,
        type: "request",
        created_time: r.updated_at || r.created_at || r.date
      }));

      const formattedAppreciations = appreciationRows.map((a) => ({
        id: `app-${a.id}`,
        type: "appreciation",
        title: "Appreciation",
        reason: "Appreciation",
        message: a.message,
        manager_name: a.manager_name || "Manager",
        created_time: a.created_at,
        date: a.created_at,
        status: "appreciation"
      }));

      const merged = [...formattedAppreciations, ...formattedRequests].sort(
        (a, b) => new Date(b.created_time || 0) - new Date(a.created_time || 0)
      );

      setMessages(merged);
    } catch (err) {
      console.error("Inbox load failed:", err);
      setError("Failed to load inbox");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusUI = (row) => {
    if (row.type === "appreciation") {
      return {
        text: "Appreciated",
        color: "#B45309",
        bg: "#FEF3C7",
        border: "#FCD34D",
        icon: <HiOutlineTrophy />
      };
    }

    const value = String(row.status_label || row.status || "").toLowerCase();

    if (value === "approved") {
      return {
        text: "Approved",
        color: theme.success,
        bg: "#DCFCE7",
        border: "#BBF7D0",
        icon: <HiOutlineCheckCircle />
      };
    }

    if (value === "declined") {
      return {
        text: "Declined",
        color: theme.danger,
        bg: "#FEE2E2",
        border: "#FECACA",
        icon: <HiOutlineXCircle />
      };
    }

    return {
      text: "Pending",
      color: theme.warning,
      bg: "#FEF3C7",
      border: "#FDE68A",
      icon: <HiOutlineClock />
    };
  };

  const getCommentText = (row) => {
    if (row.type === "appreciation") {
      return `${row.manager_name || "Manager"} appreciated your work.`;
    }

    if (row.comment) return row.comment;
    if (String(row.status).toLowerCase() === "approved") {
      return "Approved request unlocks that specific date.";
    }
    if (String(row.status).toLowerCase() === "declined") {
      return "Request denied by manager.";
    }
    return "Waiting for manager response.";
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-GB");
  };

  const filteredMessages = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return messages;

    return messages.filter((row) => {
      const haystack = [
        row.reason,
        row.comment,
        row.status,
        row.status_label,
        row.date,
        row.message,
        row.manager_name
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [messages, search]);

  const counts = useMemo(() => {
    return {
      total: messages.length,
      pending: messages.filter((m) => String(m.status).toLowerCase() === "pending").length,
      approved: messages.filter((m) => String(m.status).toLowerCase() === "approved").length,
      appreciated: messages.filter((m) => m.type === "appreciation").length
    };
  }, [messages]);

  return (
    <div style={page}>
      <div style={hero}>
        <div>
          <h2 style={title}>Inbox</h2>
          <p style={subtitle}>Track requests and appreciation messages in one place.</p>
        </div>

        <button onClick={loadInbox} style={refreshBtn}>
          <HiOutlineArrowPath style={{ fontSize: 18 }} />
          Refresh
        </button>
      </div>

      <div style={summaryRow}>
        <div style={summaryCard}>
          <span style={summaryLabel}>Total</span>
          <span style={summaryValue}>{counts.total}</span>
        </div>
        <div style={summaryCard}>
          <span style={summaryLabel}>Pending</span>
          <span style={summaryValue}>{counts.pending}</span>
        </div>
        <div style={summaryCard}>
          <span style={summaryLabel}>Approved</span>
          <span style={summaryValue}>{counts.approved}</span>
        </div>
        <div style={summaryCard}>
          <span style={summaryLabel}>Appreciated</span>
          <span style={summaryValue}>{counts.appreciated}</span>
        </div>
      </div>

      <div style={toolbar}>
        <div style={searchWrap}>
          <HiOutlineMagnifyingGlass style={searchIcon} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search inbox"
            style={searchInput}
          />
        </div>
      </div>

      {loading && (
        <div style={stateCard}>
          <HiOutlineInboxArrowDown style={{ fontSize: 28, color: theme.primary }} />
          <p style={stateText}>Loading inbox...</p>
        </div>
      )}

      {!loading && error && (
        <div style={errorCard}>
          <HiOutlineXCircle style={{ fontSize: 20, flexShrink: 0 }} />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && filteredMessages.length === 0 && (
        <div style={emptyCard}>
          <HiOutlineInboxArrowDown style={{ fontSize: 34, color: theme.primary }} />
          <h3 style={emptyTitle}>No messages found</h3>
          <p style={emptyText}>
            Your request updates and appreciation notes will appear here.
          </p>
        </div>
      )}

      {!loading && !error && filteredMessages.length > 0 && (
        <div style={listCard}>
          {filteredMessages.map((row, index) => {
            const statusUI = getStatusUI(row);

            return (
              <div
                key={row.id}
                style={{
                  ...listRow,
                  borderBottom:
                    index === filteredMessages.length - 1
                      ? "none"
                      : `1px solid ${theme.border}`
                }}
              >
                <div style={leftDotWrap}>
                  <div
                    style={{
                      ...statusDot,
                      background:
                        row.type === "appreciation"
                          ? "#D97706"
                          : statusUI.text === "Approved"
                          ? theme.success
                          : statusUI.text === "Declined"
                          ? theme.danger
                          : theme.warning
                    }}
                  />
                </div>

                <div style={mainContent}>
                  <div style={subjectText}>
                    {row.type === "appreciation" ? (
                      <span style={appreciationTitleWrap}>
                        <HiOutlineTrophy style={{ fontSize: 18, color: "#D97706" }} />
                        Appreciation
                      </span>
                    ) : (
                      row.reason || "Late entry request"
                    )}
                  </div>

                  <div style={previewText}>
                    {row.type === "appreciation" ? row.message : getCommentText(row)}
                  </div>
                </div>

                <div style={rightColumn}>
                  <div style={dateBadge}>
                    <HiOutlineCalendarDays style={{ fontSize: 14 }} />
                    {formatDate(row.date)}
                  </div>

                  <div
                    style={{
                      ...statusBadge,
                      background: statusUI.bg,
                      color: statusUI.color,
                      borderColor: statusUI.border
                    }}
                  >
                    <span style={{ display: "flex", fontSize: 15 }}>{statusUI.icon}</span>
                    {statusUI.text}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && filteredMessages.length > 0 && (
        <div style={footerNote}>
          Approved requests unlock only the selected date for production entry.
        </div>
      )}
    </div>
  );
}

/* ================= STYLES ================= */

const page = {
  fontFamily: "sans-serif",
  color: theme.text
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  marginBottom: "20px",
  flexWrap: "wrap"
};

const title = {
  margin: 0,
  fontSize: "28px",
  fontWeight: 700
};

const subtitle = {
  margin: "8px 0 0 0",
  color: theme.textMuted,
  fontSize: "14px"
};

const refreshBtn = {
  border: "none",
  borderRadius: "14px",
  padding: "12px 18px",
  background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  boxShadow: "0 10px 20px rgba(15,118,110,0.18)"
};

const summaryRow = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "14px",
  marginBottom: "18px"
};

const summaryCard = {
  background: theme.surface,
  borderRadius: "16px",
  border: "1px solid #F1F5F9",
  padding: "14px 16px",
  boxShadow: "0 8px 20px rgba(15,23,42,0.05)",
  display: "flex",
  flexDirection: "column",
  gap: "6px"
};

const summaryLabel = {
  fontSize: "12px",
  fontWeight: 700,
  color: theme.textMuted
};

const summaryValue = {
  fontSize: "24px",
  fontWeight: 800,
  color: theme.text
};

const toolbar = {
  marginBottom: "18px"
};

const searchWrap = {
  position: "relative",
  maxWidth: "360px"
};

const searchIcon = {
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: theme.textMuted,
  fontSize: "18px"
};

const searchInput = {
  width: "100%",
  height: "44px",
  borderRadius: "14px",
  border: `1px solid ${theme.border}`,
  background: "#fff",
  padding: "0 14px 0 38px",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box"
};

const stateCard = {
  background: theme.surface,
  borderRadius: "22px",
  padding: "48px 24px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center"
};

const stateText = {
  marginTop: "12px",
  color: theme.textMuted,
  fontSize: "14px"
};

const errorCard = {
  background: "#FEF2F2",
  border: "1px solid #FECACA",
  color: theme.danger,
  borderRadius: "16px",
  padding: "14px 16px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontWeight: 600
};

const emptyCard = {
  background: theme.surface,
  borderRadius: "22px",
  padding: "56px 24px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
  textAlign: "center"
};

const emptyTitle = {
  margin: "14px 0 8px 0",
  fontSize: "22px"
};

const emptyText = {
  margin: 0,
  color: theme.textMuted,
  fontSize: "14px",
  lineHeight: 1.6,
  maxWidth: "560px",
  marginInline: "auto"
};

const listCard = {
  background: theme.surface,
  borderRadius: "20px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
  overflow: "hidden"
};

const listRow = {
  display: "grid",
  gridTemplateColumns: "18px minmax(0, 1fr) 300px",
  gap: "16px",
  alignItems: "center",
  padding: "18px 20px",
  minHeight: "88px",
  boxSizing: "border-box"
};

const leftDotWrap = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const statusDot = {
  width: "10px",
  height: "10px",
  borderRadius: "999px"
};

const mainContent = {
  minWidth: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "8px"
};

const subjectText = {
  fontSize: "18px",
  fontWeight: 700,
  color: theme.text,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%"
};

const previewText = {
  color: theme.textMuted,
  fontSize: "13px",
  lineHeight: 1.35,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%"
};

const appreciationTitleWrap = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px"
};

const rightColumn = {
  width: "300px",
  display: "grid",
  gridTemplateColumns: "128px 140px",
  justifyContent: "end",
  alignItems: "center",
  gap: "12px",
  flexShrink: 0
};

const dateBadge = {
  width: "128px",
  height: "40px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 700,
  border: "1px solid #CBD5E1",
  background: "#F8FAFC",
  color: theme.textMuted,
  whiteSpace: "nowrap",
  boxSizing: "border-box"
};

const statusBadge = {
  width: "140px",
  height: "40px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 700,
  border: "1px solid",
  whiteSpace: "nowrap",
  boxSizing: "border-box"
};

const footerNote = {
  marginTop: "16px",
  color: theme.textMuted,
  fontSize: "13px",
  textAlign: "center"
};

export default Inbox;